import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEqual } from 'lodash';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ConditionalTooltip,
  FormInput,
  FormInputValueTypes,
  FormSection,
  FormCheckbox,
  Modal,
} from '@praktio/common-ui';

import SignupCodes from '../SignupCodes';
import CourseOverrides from './CourseOverrides';

import { TopNavBranding, TopNavBrandingPreviewWrapper } from '../../TopNav';
import { RootState } from '../../../ducks/store';
import { addTeam, deactivateTeam, deleteTeam, updateTeamSettings } from '../../../ducks/teams';
import DeleteModal from '../modals/DeleteModal/DeleteModal';
import { useQuery, getLocalDateTime } from '../../../utils/functions';
import { Timezone } from '../../../utils/constants';
import { OverlayTrigger, Popover } from 'react-bootstrap';

interface ITeamSettingsTabProps {
  notifyHasChanges: (changes: boolean) => void;
  setSavedChanges: (success: boolean) => void;
}

const TeamSettingsTab = forwardRef<IForwardProps, ITeamSettingsTabProps>(
  ({ notifyHasChanges, setSavedChanges }, ref) => {
    useImperativeHandle(ref, () => ({
      save,
    }));

    const params = useParams();
    const teamId = params.teamId as string;

    const query = useQuery();
    const parentId = query.get('parentId');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
      team,
      teamLogoIsFrom,
      deactivatedAncestor,
      courseOverridesAreFrom,
      ownsCourseOverrides,
      overridableCourses,
      inheritedCourseOverrides,
      coursesAreLoaded,
      initialSettings,
      logoInheritedTeamId,
    } = useSelector((state: RootState) => {
      const { teams, courses } = state;
      const { current } = teams;
      const team = current || ({} as ITeam);
      const {
        name,
        useWhiteFilter,
        isUnlocked,
        enableTimeRelease,
        ignoreExpiration,
        expirationOverride,
        logoInheritedTeamId,
        logo,
        signupCodes,
        deactivatedAncestorId,
        isMarmonTeam,
        courseOverrides,
        inheritedCourseOverrides,
        overridesInheritedTeamId,
      } = team;

      // An override can be set against any course, so the picker offers the whole catalog. Team.tsx
      // already dispatches getCourses() on mount for the Courses tab, so this costs no extra fetch
      // and saves the API walking the team tree to work out which courses its subteams carry.
      // Ordering is CourseOverrides' concern — it sorts both the picker and the rows for display.
      const overridableCourses = (courses.list as ICourse[]) || [];

      // Course overrides are owned by the top-level team; a subteam displays what it inherits.
      // `parentId` covers the new-team route, where `team` is an empty object because Team.tsx
      // clears the current team — without it a new SUBTEAM would render as editable. The section is
      // hidden entirely on a new team regardless (see `isNewTeam` below): addTeam does not persist
      // overrides, so anything typed there would be discarded behind a success message.
      const ownsCourseOverrides = !(team.parent || parentId);

      const settings: ITeamSettings = {
        name,
        useWhiteFilter: !!useWhiteFilter,
        isUnlocked: !!isUnlocked,
        enableTimeRelease: !!enableTimeRelease,
        ignoreExpiration: !!ignoreExpiration,
        expirationOverride,
        logo,
        signupCodes,
        isMarmonTeam,
        // Only the overrides that actually exist — a course with no override has no row, so a
        // catalog of 30-40 courses does not become 30-40 empty text boxes. Rows are added through
        // the picker and removed with the control on the row.
        ...(ownsCourseOverrides
          ? {
              courseOverrides: (courseOverrides || []).map((o: ICourseOverride) => ({
                courseId: o.courseId?.toString() as string,
                description: o.description || '',
              })),
            }
          : {}),
      };

      let teamLogoIsFrom: ITeam | undefined;
      let deactivatedAncestor: ITeam | undefined;
      let courseOverridesAreFrom: ITeam | undefined;

      teams.list?.forEach((team) => {
        if (team._id === logoInheritedTeamId) {
          teamLogoIsFrom = team;
        }

        if (team._id === deactivatedAncestorId) {
          deactivatedAncestor = team;
        }

        if (team._id === overridesInheritedTeamId) {
          courseOverridesAreFrom = team;
        }
      });
      return {
        team,
        teamLogoIsFrom,
        deactivatedAncestor,
        courseOverridesAreFrom,
        ownsCourseOverrides,
        overridableCourses,
        // The API returns an ancestor's values under their own key, so a subteam's read-only rows
        // can never be confused with the team's own editable set.
        inheritedCourseOverrides: inheritedCourseOverrides || [],
        // Distinguishes "the catalog has not arrived" from "there are no courses" — Team.tsx fires
        // getCourses() and getTeam() together with no ordering, and the team usually wins.
        coursesAreLoaded: !!courses.list,
        initialSettings: settings,
        logoInheritedTeamId,
      };
    });

    const prevSettings = useRef<ITeamSettings>(initialSettings);
    const [settings, setSettings] = useState<ITeamSettings>(initialSettings);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
      if (initialSettings && !isEqual(prevSettings.current, initialSettings)) {
        setSettings(initialSettings);
        if (notifyHasChanges) {
          notifyHasChanges(false);
        }
      }

      prevSettings.current = initialSettings;
    }, [initialSettings]);

    const hasChanges = !isEqual(settings, prevSettings.current);
    notifyHasChanges(hasChanges);

    // Used by parent component to initiate a save
    const save = async () => {
      try {
        const updatedSettings = cloneDeep(settings);
        if (teamId && teamId !== 'new') {
          await dispatch(updateTeamSettings(teamId, updatedSettings));
        } else {
          await dispatch(addTeam({ ...updatedSettings, parent: parentId }, navigate));
        }

        setSavedChanges(true);
      } catch (e) {
        setSavedChanges(false);
      }
    };

    const onFormInputChange = ({
      fieldName,
      value,
    }: {
      fieldName?: string;
      value?: FormInputValueTypes | ICourse;
    }) => {
      if (fieldName) {
        const updatedSettings = cloneDeep(settings);
        switch (fieldName) {
          case 'logo':
            if (value instanceof FileList || !value) {
              updatedSettings.logo = value ? (value as FileList)[0] : undefined;
            }
            break;
          default:
            (updatedSettings as any)[fieldName] = value;
        }
        setSettings(updatedSettings);
      }
    };

    const onSignupCodeChange = (data: ISignupCode, isDelete?: boolean) => {
      const updatedSettings = cloneDeep(settings);
      if (!updatedSettings.signupCodes) {
        updatedSettings.signupCodes = [];
      }

      const index = updatedSettings.signupCodes.findIndex((s) => s._id === data._id);
      if (index > -1) {
        if (isDelete) {
          updatedSettings.signupCodes.splice(index, 1);
        } else {
          updatedSettings.signupCodes.splice(index, 1, data);
        }
      } else {
        updatedSettings.signupCodes.push(data);
      }

      setSettings(updatedSettings);
    };

    const {
      name,
      isUnlocked,
      enableTimeRelease,
      useWhiteFilter,
      logo,
      ignoreExpiration,
      expirationOverride,
      signupCodes,
      isMarmonTeam,
      courseOverrides,
    } = settings;

    const { isDeactivated } = team;

    let logoSource: any;
    if (logo) {
      logoSource = typeof logo === 'object' ? URL.createObjectURL(logo) : logo;
    }

    const [showAddSignupCodeModal, setShowAddSignupCodeModal] = useState(false);
    const [showAddOverrideModal, setShowAddOverrideModal] = useState(false);

    // Matches the check `save` already makes before choosing addTeam over updateTeamSettings.
    const isNewTeam = !teamId || teamId === 'new';
    const activateString = `${isDeactivated ? 'R' : 'D'}eactivate`;
    return (
      <>
        <FormSection sectionName="Team Information">
          <FormInput
            value={name}
            fieldName="name"
            label="Name"
            placeholder="Team Name"
            singleLine
            onChange={(event: any) =>
              onFormInputChange({
                fieldName: 'name',
                value: event?.target.value,
              })
            }
          />
          {teamId && (
            <FormInput value={teamId} fieldName="teamId" label="Team ID" displayOnly singleLine />
          )}
          <FormInput
            disabled={!!logoInheritedTeamId}
            fieldName="logo"
            label="Branding"
            subtext="100 x 200px png file"
            value=""
            secondaryButton
            type="file"
            onChange={(event: { target: any }) =>
              onFormInputChange({ fieldName: 'logo', value: event.target.files })
            }
            fileSelectButtonText={logoSource ? 'Replace' : 'Upload'}
            singleLine
            fileSource={logoSource}
            filePreviewClassName="BrandingPreview"
            previewComponent={
              logoSource && (
                <TopNavBrandingPreviewWrapper className="mb-3">
                  <TopNavBranding
                    collaborationLogo={logoSource}
                    useWhiteFilter={useWhiteFilter}
                    className="d-flex"
                    isPreview
                  />
                </TopNavBrandingPreviewWrapper>
              )
            }
            secondaryOption={
              logoSource && (
                <FormCheckbox
                  id="use-white-filter"
                  label="Apply White Filter"
                  disabled={!!logoInheritedTeamId}
                  checked={useWhiteFilter}
                  onChange={() =>
                    onFormInputChange({
                      fieldName: 'useWhiteFilter',
                      value: !useWhiteFilter,
                    })
                  }
                />
              )
            }
            additionalInfo={
              logoInheritedTeamId ? (
                <div className="mt-3">
                  The above branding is inherited from the{' '}
                  <Link to={`/admin/teams/${logoInheritedTeamId}`}>{teamLogoIsFrom?.name}</Link>{' '}
                  team and can only be adjusted in that team's settings.
                </div>
              ) : undefined
            }
          >
            {logoSource && (
              <Button
                onClick={() => onFormInputChange({ fieldName: 'logo', value: undefined })}
                className="secondary"
                disabled={!!logoInheritedTeamId}
              >
                Clear
              </Button>
            )}
          </FormInput>
          <FormInput
            type="radio"
            fieldName="isUnlocked"
            label="Learner Navigation"
            singleLine
            inlineChoices
            value={isUnlocked ? 'unlocked' : 'locked'}
            radioChoices={[
              {
                label: 'Locked',
                value: 'locked',
              },
              {
                label: 'Unlocked',
                value: 'unlocked',
              },
            ]}
            onChange={(event: any) =>
              onFormInputChange({
                fieldName: 'isUnlocked',
                value: event.target.value === 'unlocked' ? true : false,
              })
            }
          />
          <FormInput
            type="radio"
            fieldName="enableTimeRelease"
            label="Time Released Modules"
            singleLine
            inlineChoices
            value={enableTimeRelease ? 'timed' : 'immediate'}
            radioChoices={[
              {
                label: 'Immediately Released',
                value: 'immediate',
              },
              {
                label: 'Time Released',
                value: 'timed',
              },
            ]}
            onChange={(event: any) =>
              onFormInputChange({
                fieldName: 'enableTimeRelease',
                value: event.target.value === 'timed' ? true : false,
              })
            }
          />
          <FormInput
            type="radio"
            fieldName="ignoreExpiration"
            label="Course Access Expiration"
            subtext="Use these settings to adjust access expiration for courses assigned to this team."
            inlineChoices
            value={ignoreExpiration ? 'indefinite' : !!expirationOverride ? 'specific' : 'onDate'}
            radioChoices={[
              {
                id: 'default-radio',
                label: (
                  <div className="radio-label-w-tool">
                    Course-level setting
                    <OverlayTrigger
                      overlay={
                        <Popover id="course-level-setting">
                          <Popover.Content>
                            Default to the expiration set on each assigned course, with any specific
                            expiration set for a learner taking precedence.
                          </Popover.Content>
                        </Popover>
                      }
                    >
                      <span className="material-icons-outlined">&#xe8fd;</span>
                    </OverlayTrigger>
                  </div>
                ) as any,
                value: 'onDate',
              },
              {
                id: 'indefinite-radio',
                label: (
                  <div className="radio-label-w-tool">
                    Indefinite
                    <OverlayTrigger
                      overlay={
                        <Popover id="indefinite-setting">
                          <Popover.Content>
                            Each course assigned to this team will not expire, regardless of its
                            course-level expiration setting.
                          </Popover.Content>
                        </Popover>
                      }
                    >
                      <span className="material-icons-outlined">&#xe8fd;</span>
                    </OverlayTrigger>
                  </div>
                ) as any,
                value: 'indefinite',
              },
              {
                id: 'specific-radio',
                label: (
                  <div className="radio-label-w-tool">
                    Specific date
                    <OverlayTrigger
                      overlay={
                        <Popover id="specific-date-setting">
                          <Popover.Content>
                            Each course assigned to this team will expire on the specified date,
                            regardless of its course-level expiration setting.
                          </Popover.Content>
                        </Popover>
                      }
                    >
                      <span className="material-icons-outlined">&#xe8fd;</span>
                    </OverlayTrigger>
                  </div>
                ) as any,
                value: 'specific',
              },
            ]}
            onChange={(e: any) => {
              let ignoreExpiration = false;
              let expirationOverride: Date | undefined = undefined;
              const updatedSettings = cloneDeep(settings);
              switch (e.target.value) {
                case 'indefinite':
                  ignoreExpiration = true;
                  break;
                case 'specific':
                  expirationOverride = new Date();
                  expirationOverride.setFullYear(expirationOverride.getFullYear() + 1);
                  expirationOverride.setDate(expirationOverride.getDate() - 1);
                  break;
                default:
                  break;
              }

              updatedSettings.ignoreExpiration = ignoreExpiration;
              updatedSettings.expirationOverride = expirationOverride;
              setSettings(updatedSettings);
            }}
          />
          {(!!expirationOverride || ignoreExpiration) && (
            <div className="exp-inputs">
              {!!expirationOverride && (
                <FormInput
                  value={getLocalDateTime(expirationOverride)}
                  fieldName="expirationOverride"
                  label="Choose Date"
                  className="expiration-override"
                  type="datetime-local"
                  subtext={`Dates and times display in your local time (${Timezone})`}
                  onChange={(e: any) => {
                    const updatedSettings = cloneDeep(settings);
                    let value = e.target.value;

                    if (!value) {
                      updatedSettings.ignoreExpiration = false;
                    }

                    updatedSettings.expirationOverride = value;
                    setSettings(updatedSettings);
                  }}
                />
              )}
            </div>
          )}
        </FormSection>
        <FormSection
          sectionName="Signup Codes"
          sectionAddition={
            <Button className="secondary" onClick={() => setShowAddSignupCodeModal(true)}>
              ADD CODE
            </Button>
          }
        >
          <SignupCodes
            team={team || ({} as ITeam)}
            signupCodes={signupCodes}
            onApply={(data, isDelete) => onSignupCodeChange(data, isDelete)}
            showAddSignupCodeModal={showAddSignupCodeModal}
            setShowAddSignupCodeModal={setShowAddSignupCodeModal}
          />
          {(signupCodes?.length ?? 0) > 0 && (
            <div className="mt-2">
              <span className="required-notice">* your local time zone ({Timezone})</span>
            </div>
          )}
          <div className="d-flex w-100 mt-3">
            <Link to={`/signup`}>Go to Signup Page</Link>
          </div>
        </FormSection>
        {/* Hidden until the team exists: addTeam does not persist courseOverrides, so a section
            offered here would silently discard whatever was typed behind a "Team added" message. */}
        {isNewTeam ? null : (
          <FormSection
            sectionName="Course Overrides"
            sectionDescription={
              ownsCourseOverrides
                ? 'Give a course its own description for this team. Learners in this team, and in any team beneath it, see this description instead of the one on the course.'
                : 'Course descriptions for this team are set on the team it belongs to and cannot be changed here.'
            }
            sectionAddition={
              ownsCourseOverrides && coursesAreLoaded ? (
                <Button className="secondary" onClick={() => setShowAddOverrideModal(true)}>
                  ADD OVERRIDE
                </Button>
              ) : undefined
            }
          >
            <CourseOverrides
              ownsOverrides={ownsCourseOverrides}
              overridableCourses={overridableCourses}
              coursesAreLoaded={coursesAreLoaded}
              overrides={(ownsCourseOverrides ? courseOverrides : inheritedCourseOverrides) || []}
              inheritedFrom={courseOverridesAreFrom}
              onChange={(updated) =>
                onFormInputChange({ fieldName: 'courseOverrides', value: updated as any })
              }
              showAddModal={showAddOverrideModal}
              setShowAddModal={setShowAddOverrideModal}
            />
          </FormSection>
        )}
        <FormSection sectionName="Marmon Reporting">
          <FormCheckbox
            id="marmon-check"
            onChange={() =>
              onFormInputChange({
                fieldName: 'isMarmonTeam',
                value: !isMarmonTeam,
              })
            }
            label="Enable Marmon Reporting"
            checked={isMarmonTeam || false}
          />
        </FormSection>
        <FormSection sectionName="Deactivate or Delete Team" lastSection>
          <FormInput
            label={`${activateString} Team`}
            fieldName="deactivateTeam"
            singleLine
            subtext={
              deactivatedAncestor
                ? `Team is deactivated through '${deactivatedAncestor.name}'`
                : `Team can be ${activateString.toLowerCase()}d.`
            }
            type="button"
            buttonText={activateString}
            buttonClassName="secondary"
            onChange={() => setShowDeactivateModal(true)}
            disabled={!!deactivatedAncestor}
          />

          <FormInput
            label="Delete Team"
            fieldName="deleteTeam"
            singleLine
            subtext="Permanently delete this Team from Praktio. This cannot be undone."
            type="button"
            buttonText="Delete"
            buttonClassName="secondary"
            onChange={() => setShowDeleteModal(true)}
            lastInSection
          />
        </FormSection>
        <Modal
          onHide={() => setShowDeactivateModal(false)}
          onSave={() => dispatch(deactivateTeam({ teamId }))}
          show={showDeactivateModal}
          confirmationString={activateString.toUpperCase()}
          title={`${activateString} Team`}
          description={`Are you sure you want to ${activateString.toLowerCase()} the Team "${
            team.name
          }"?`}
        />
        <DeleteModal
          type="Team"
          name={team.name as string}
          onHide={() => setShowDeleteModal(false)}
          show={showDeleteModal}
          dispatchAction={deleteTeam}
          dispatchData={{ teamId, navigate }}
        />
      </>
    );
  },
);

export default TeamSettingsTab;
